/* eslint-disable import-x/no-namespace */
import { useMemo } from 'react';

import type { PointGetCardsFilters } from '@vlabs/api/types';
import { Control } from '@vlabs/uikit';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';

import { filterIsApplied, FilterMenu } from '@vlabs/monokit/shared/ui/filter-menu';

import * as sideMenuFilters from './side-menu';
import { useCardsFilters } from './useCardsFilters';
import { FIO_FILTER_KEY } from './utils';

interface CardListFilterMenuProps {
  exclude?: (keyof PointGetCardsFilters)[];
}

function CardListFilterMenu({
  exclude = undefined,
}: CardListFilterMenuProps): JSX.Element {
  const { t } = useTranslation();

  const { filters, resetFilters } = useCardsFilters();
  const isResetDisabled = Object.values(omit(filters, 'page', 'page_size', ...(exclude ?? []))).filter((v) => v !== undefined).length === 0;

  const $filters = useMemo(() => {
    const result = [];
    if (!exclude?.includes('card_ids')) {
      result.push(<sideMenuFilters.ExternalIdFilterWithLeftPopover
        badge={filterIsApplied(!!filters.card_ids)}
        key="card_ids"
        testId="card_ids-Filter"
        value={t('card-list-page:filters.popovers.external id.label')}
      />);
    }
    if (!exclude?.includes(FIO_FILTER_KEY)) {
      result.push(<sideMenuFilters.FioFilterWithLeftPopover
        badge={filterIsApplied(!!filters[FIO_FILTER_KEY])}
        key="user_data"
        testId="user_data-Filter"
        value={t('card-list-page:filters.popovers.fio.label')}
      />);
    }
    if (!exclude?.includes('create_time__gte') && !exclude?.includes('create_time__lt')) {
      result.push(<sideMenuFilters.DateFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.create_time__lt ?? filters.create_time__gte))}
        key="create_time"
        testId="create_time-Filter"
        value={t('card-list-page:filters.popovers.date.label')}
      />);
    }
    if (!exclude?.includes('list_ids')) {
      result.push(<sideMenuFilters.ListsFilterWithLeftPopover
        badge={filterIsApplied(!!filters.list_ids)}
        key="list_ids"
        testId="list_ids-Filter"
        value={t('card-list-page:filters.popovers.lists.label')}
      />);
    }
    return result;
  }, [exclude, filters, t]);

  return (
    <FilterMenu
      filters={$filters}
      footer={(
        <Control.Button
          data-testid="button.reset-cards-filters"
          disabled={isResetDisabled}
          fullWidth
          kind="negative"
          onClick={() => resetFilters(exclude)}
          type="button"
        >
          {t('card-list-page:filters.buttons.reset button.label')}
        </Control.Button>
      )}
    />
  );
}

export { CardListFilterMenu };
